<script lang="ts" setup>
import Breadcrumbs from '~/components/Breadcrumbs.vue'
import AccountLayout from '~/components/Account/AccountLayout.vue'
import TrackingMapIcon from '~/assets/icons/tracking-map.svg'
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import { useLoaderStore } from '~/store/loader'
import SiteProContentTables from '~/components/SitePro/SiteProContentTables.vue'
import { Routes } from '~/enums/Routes'
import ForwardRequestTabsEnum from '~/enums/ForwardRequestTabsEnum'
import type { IClientInterface } from '~/types/auth/identityTypes'
import { useIdentity } from '~/store/identity'

const shipzeeProContent = useSiteProContentStore()
const { t } = useLang()
const loaderStore = useLoaderStore()
const { isShipzee, isWhitelabel } = useTheme()
loaderStore.setLoader(true)
await shipzeeProContent.fetch()
loaderStore.setLoader(false)

const identity = useIdentity()
const user = ref<IClientInterface | null>(null)
user.value = await identity.getUser()

onMounted(() => {
  if (user.value?.unpaid_frs_count) {
    shipzeeProContent.setActiveTab(ForwardRequestTabsEnum.AllShipmentsTab)
  }
})

const breadcrumbs = computed(() => {
  return [
    { id: Routes.Start, translation: t('breadcrumb.start'), isFullPath: true },
    {
      id: Routes.Account.SitePro,
      translation: t('site_pro.pro_us_address'),
      isFullPath: true,
    },
  ]
})

const haveOrders = computed(() => {
  return !!(
    shipzeeProContent.readyItems.length ||
    shipzeeProContent.inReviewItems.length ||
    shipzeeProContent.allItems.length
  )
})

definePageMeta({
  layout: false,
  middleware: ['auth', 'marketplace'],
})
</script>

<template>
  <div>
    <NuxtLayout name="page">
      <div>
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
        <AccountLayout no-background :no-back-button="isWhitelabel">
          <template #heading>{{ t('site_pro.pro_us_address') }}</template>

          <div class="pro-content xl:bg-neutral8 dark:bg-dmGray1 rounded-md">
            <div
              class="pro-info flex flex-col xl:flex-row p-4 xl:p-5 gap-4 xl:gap-6 bg-neutral8 dark:bg-dmGray1 rounded-md">
              <div
                class="flex flex-col gap-4 xl:gap-0 xl:flex-row bg-white dark:bg-dmGray2 rounded-md p-3 basis-7/12 wl:px-0">
                <YourUsAddress class="basis-1/2" />

                <div v-if="isShipzee" class="bg-neutral7 dark:bg-dmGray1 p-4 rounded-sm basis-1/2">
                  <img :src="TrackingMapIcon" alt="" class="pb-3 dark:invert" />
                  <p
                    class="text-sm font-base text-text2 dark:text-white"
                    v-html="t('site_pro.its_your_personal_us_address_description')" />
                </div>
              </div>

              <div class="flex flex-col self-start basis-5/12 w-full">
                <ClientOnly fallback-tag="div">
                  <ShipmentCounts />
                </ClientOnly>
              </div>
            </div>

            <ClientOnly fallback-tag="div">
              <div class="h-[1px] xl:bg-neutral6 my-4 mx-5 wl:mx-0 wl:my-1" />
              <div class="bg-neutral8 rounded-md dark:bg-dmGray1 wl:bg-white wl:px-0">
                <SiteProContentTables v-if="haveOrders === true" />

                <LazyEmptyShipmentsTable v-else class="p-5" />
              </div>
            </ClientOnly>
          </div>
        </AccountLayout>
      </div>
    </NuxtLayout>
  </div>
</template>

<style lang="scss" scoped>
.wl {
  .pro-content {
    &,
    .pro-info {
      @apply bg-white p-0;
    }

    .pro-divider {
      @apply hidden lg:flex bg-neutral7;
    }
  }
}
</style>
